/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the body sample.

   The Studio does not edit spec CONTENT (that is the Designer's job on a
   real document); it edits the FORMATTING RULES a level applies. So the
   body here is a realistic sample, GENERATED FROM THE OPEN FORMA'S OWN
   LEVELS (`sampleSpec.ts` — as many levels deep as the forma defines, not
   a fixed snippet), rendered through the exact same brain the export uses
   (`shared/body-layout.ts`) — the identical CSS classes and inline styles
   `numbering.ts` decorates the Designer's live document with (`dsg-block`,
   `dsg-level-N`, `dsg-num`), so a forma edit restyles this sample exactly
   as it would restyle real content.

   Clicking a sample paragraph points the Levels palette at that level —
   the fastest path from "I see it wrong" to "fix it" for a lay person.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import {
  applyTextCase,
  bodyParagraphCss,
  computeNumbering,
  numberColumnPx,
  resolveBodyLayout,
} from '../../shared/body-layout';
import type { FormaEdit } from '../designer/DesignPalettes';
import { generateStudioSampleBlocks, sampleMaxLevel } from './sampleSpec';

/* ─── Level spacing, dragged in place (restored 2026-08-13) ───
   The classic canvas's LevelRow put thin grips in the gaps around the active
   level: drag the gap above it = Space Before, below it = Space After, and
   the gap BETWEEN two paragraphs of the same level = the same-level gap
   (Word derives that gap from Space After, so that bar edits Space After —
   exactly the classic semantics). Same control language as the zone bars
   (ZoneSpacer): thin accent bar, ns-resize, 2px ≈ 1pt, live pt readout. */
function LevelSpacer({
  which,
  levelIdx,
  valuePt,
  noSpaceSame,
  insetLeftPx,
  centerPx,
  edit,
}: {
  which: 'before' | 'after' | 'same';
  levelIdx: number;
  /** The pt value this bar edits/reads (spaceBefore or spaceAfter). */
  valuePt: number;
  /** Same-level gap suppressed by "No space same" — pill instead of a bar. */
  noSpaceSame?: boolean;
  insetLeftPx: number;
  /** Where the grip centres, relative to this zero-height host. */
  centerPx: number;
  edit: FormaEdit;
}) {
  const [hover, setHover] = React.useState(false);
  const [drag, setDrag] = React.useState(false);
  const lit = hover || drag;
  const field = which === 'before' ? 'spaceBefore' : 'spaceAfter';

  const commit = (v: number) => {
    const nv = Math.max(0, Math.min(120, Math.round(v)));
    edit((f) => {
      const levels = [...f.levels];
      levels[levelIdx] = { ...levels[levelIdx], [field]: nv };
      return { ...f, levels };
    });
  };

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startVal = valuePt;
    setDrag(true);
    const onMove = (ev: MouseEvent) => commit(startVal + (ev.clientY - startY) * 0.5);
    const onUp = () => {
      setDrag(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const label =
    which === 'before'
      ? 'Space Before'
      : which === 'after'
        ? 'Space After'
        : 'Space Between (edits Space After)';

  if (which === 'same' && noSpaceSame) {
    return (
      <div className="sfs-levelspacer" aria-hidden="true">
        <span
          className="sfs-levelspacer-pill"
          style={{ left: insetLeftPx, top: centerPx }}
          title='"No space same" is on for this level — same-level paragraphs keep no gap'
        >
          no space
        </span>
      </div>
    );
  }

  return (
    <div className="sfs-levelspacer">
      <div
        role="slider"
        tabIndex={0}
        aria-label={`Level ${levelIdx + 1} ${label.toLowerCase()}`}
        aria-valuenow={valuePt}
        aria-valuemin={0}
        aria-valuemax={120}
        title={`${label} — drag, or set in the Levels panel (${valuePt}pt)`}
        className={`sfs-levelspacer-grip${lit ? ' is-lit' : ''}${valuePt > 0 ? ' has-space' : ''}`}
        style={{ left: insetLeftPx, top: centerPx }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseDown={startDrag}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            commit(valuePt + (e.key === 'ArrowUp' ? 1 : -1));
          }
        }}
      >
        {lit && (
          <span className="sfs-levelspacer-readout">
            {label} · {valuePt}pt
          </span>
        )}
      </div>
    </div>
  );
}

function cssVarStyle(css: ReturnType<typeof bodyParagraphCss>): React.CSSProperties {
  return {
    paddingLeft: css.paddingLeft,
    textIndent: css.textIndent,
    marginTop: css.marginTop,
    marginBottom: css.marginBottom,
    textAlign: css.textAlign,
    fontWeight: css.fontWeight,
    fontStyle: css.fontStyle,
    textDecoration: css.textDecoration,
    textTransform: css.textTransform,
    ...(css.color ? { color: css.color } : {}),
  };
}

export interface StudioBodyProps {
  forma: StandardConfig | null;
  /** Which level is lit up (the Levels palette's target), or null. */
  levelFocus: number | null;
  /** Click a sample paragraph → point the Levels palette at its level. */
  onLevelClick: (level: number) => void;
  /** Present → the focused level grows the classic drag-to-space grips
   *  (Space Before / Between / After) right in its gaps. */
  edit?: FormaEdit;
  /** Override the generated sample — not used by any current caller; kept
   *  for tests / future callers that want fixed content instead of the
   *  forma-derived generator. */
  blocks?: ContentBlock[];
}

export function StudioBody({ forma, levelFocus, onLevelClick, edit, blocks }: StudioBodyProps) {
  // Regenerated straight off the forma reference every render — no stale
  // memo of "how many levels this forma had last time". Adding, removing,
  // or importing over a level changes `forma.levels.length` and the sample
  // follows immediately (contract: see sampleSpec.ts's 2026-08-13 note).
  const sampleBlocks = React.useMemo(
    () => blocks ?? generateStudioSampleBlocks(forma),
    [blocks, forma]
  );
  const maxLevel = sampleMaxLevel(forma);
  const layouts = React.useMemo(() => resolveBodyLayout(forma), [forma]);
  const refs = React.useMemo(() => computeNumbering(sampleBlocks, forma), [sampleBlocks, forma]);

  return (
    <div
      className="dsg-doc sfs-studio-body"
      role="group"
      aria-label="Sample section content"
      data-sample-level-count={maxLevel}
    >
      {sampleBlocks.map((b, i) => {
        const level = Math.max(1, Math.min(9, b.level || 1));
        const layout = layouts[level - 1];
        const prevLevel = i > 0 ? sampleBlocks[i - 1].level : null;
        const nextLevel = i < sampleBlocks.length - 1 ? sampleBlocks[i + 1].level : null;
        const css = bodyParagraphCss(layout, {
          sameStyleAsPrev: prevLevel === level,
          sameStyleAsNext: nextLevel === level,
        });
        const prefix = refs[i]?.prefix || '';
        const text = applyTextCase(b.text, layout);
        const lit = levelFocus === level;
        // The classic drag-to-space grips, in this level's own gaps: above
        // its first sample row (Space Before), between its two rows (the
        // same-level gap — edits Space After, Word's rule), below its last
        // row (Space After). Only the FOCUSED level grows them.
        const lvl = forma?.levels?.[level - 1];
        const grips = lit && edit && lvl;
        const insetLeftPx = parseFloat(css.paddingLeft) || 0;
        const mtPx = parseFloat(css.marginTop) || 0;
        const mbPx = parseFloat(css.marginBottom) || 0;
        const spacerBefore =
          grips && prevLevel !== level ? (
            <LevelSpacer
              key={`sb${i}`}
              which="before"
              levelIdx={level - 1}
              valuePt={lvl.spaceBefore || 0}
              insetLeftPx={insetLeftPx}
              centerPx={-Math.max(mtPx, 2) / 2}
              edit={edit}
            />
          ) : null;
        const spacerSame =
          grips && prevLevel === level ? (
            <LevelSpacer
              key={`sm${i}`}
              which="same"
              levelIdx={level - 1}
              valuePt={lvl.spaceAfter || 0}
              noSpaceSame={!!lvl.noSpaceSameStyle}
              insetLeftPx={insetLeftPx}
              centerPx={-Math.max(lvl.noSpaceSameStyle ? mbPx : Math.max(mtPx, mbPx), 2) / 2}
              edit={edit}
            />
          ) : null;
        const spacerAfter =
          grips && nextLevel !== level ? (
            <LevelSpacer
              key={`sa${i}`}
              which="after"
              levelIdx={level - 1}
              valuePt={lvl.spaceAfter || 0}
              insetLeftPx={insetLeftPx}
              centerPx={Math.max(mbPx, 2) / 2}
              edit={edit}
            />
          ) : null;
        return (
          <React.Fragment key={b.id}>
            {spacerBefore}
            {spacerSame}
            <div
              className={`dsg-block dsg-level-${level}${lit ? ' sfs-level-lit' : ''}`}
              style={cssVarStyle(css)}
              role="button"
              tabIndex={0}
              aria-label={`Level ${level} sample paragraph — click to style level ${level}`}
              onClick={() => onLevelClick(level)}
              onContextMenu={(e) => {
                // "Level style…" (the Designer's demotion pattern, §2.1) — the
                // sample row is never a text editor, so a right-click carries
                // the same intent as a left-click here; suppress the browser
                // menu so it reads as the app's own affordance.
                e.preventDefault();
                onLevelClick(level);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLevelClick(level);
                }
              }}
            >
              {prefix && (
                <span
                  className="dsg-num"
                  aria-hidden="true"
                  style={{ minWidth: numberColumnPx(layout) }}
                >
                  {prefix}
                </span>
              )}
              {text}
            </div>
            {spacerAfter}
          </React.Fragment>
        );
      })}
    </div>
  );
}
